import type { TablePaginationTranslations } from 'noddi-ui-web';
import {
  defaultOrganizationPrimaryContactTranslations,
  type OrganizationPrimaryContactTranslations
} from './organizationPrimaryContactTranslations';
import {
  defaultRemoveOrganizationCarTranslations,
  type RemoveOrganizationCarTranslations
} from './removeOrganizationCarTranslations';

export type NotificationGroupOverviewTranslations = RemoveOrganizationCarTranslations &
  OrganizationPrimaryContactTranslations & {
    title: string;
    description: string;
    byCar: string;
    byCarDescription: string;
    byMember: string;
    byMemberDescription: string;
    car: string;
    member: string;
    driver: string;
    driverTooltip: string;
    drives: string;
    drivesTooltip: string;
    otherCars: string;
    searchCarsTablePlaceholder: string;
    searchMembersTablePlaceholder: string;
    noDriver: string;
    peopleCount: (count: number) => string;
    carCount: (count: number) => string;
    bookingSms: string;
    serviceReminderSms: string;
    bookingSmsDescription: string;
    serviceReminderSmsDescription: string;
    smsTurnedOffWarning: string;
    smsTurnedOff: string;
    carsWithoutDriver: (count: number) => string;
    carsWithoutDriverDescription: string;
    carsWithSilencedDriver: (count: number) => string;
    carsWithSilencedDriverDescription: string;
    editDriver: string;
    driverSheetDescription: string;
    searchMembersPlaceholder: string;
    noMembersFound: string;
    otherMembers: string;
    driverSaved: (licensePlate: string) => string;
    moreCars: (count: number) => string;
    noDrivenCars: string;
    noDrivenCarsDescription: string;
    back: string;
    addCarsDescription: (name: string) => string;
    searchCars: string;
    noCarsFound: string;
    currentDriver: (name: string) => string;
    currentDriverLabel: string;
    cancel: string;
    addMember: string;
    addMemberStepNumber: string;
    addMemberStepNumberDescription: string;
    addMemberStepPerson: string;
    addMemberStepPersonDescription: string;
    addMemberStepSettings: string;
    addMemberStepSettingsDescription: string;
    next: string;
    memberFoundDescription: string;
    memberAlreadyInGroup: string;
    memberUnavailable: string;
    memberNotFound: string;
    memberEmailTaken: string;
    emailOptional: string;
    sendAddedSms: string;
    sendAddedSmsDescription: string;
    memberAdded: (name: string) => string;
    contactDetails: string;
    memberEditAdminOnly: string;
    roleAndDriversAdminOnly: string;
    adminEditsRest: string;
    viewOnly: string;
    role: string;
    groupAdmin: string;
    driverEditAdminOnly: string;
    groupAdminDescription: string;
    /** Administrator switch copy for a leasing company or car dealership. */
    holderGroupAdminDescription: string;
    employee: string;
    employeeDescription: string;
    lastAdminHint: string;
    makeAnotherAdminFirst: string;
    accountOwnedByMember: (name: string) => string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    notifications: string;
    notificationsDescription: string;
    saveChanges: string;
    edit: string;
    memberSaved: (name: string) => string;
    removeMember: string;
    removeMemberDescription: (name: string) => string;
    leaveGroup: string;
    leaveGroupDescription: string;
    remove: string;
    memberRemoved: (name: string) => string;
    primaryContactCannotBeRemoved: string;
    chooseDriverStep: string;
    chooseCarsStep: string;
    reviewStep: string;
    continueToReview: string;
    confirmDriverChange: string;
    takingOverFrom: (name: string) => string;
    alreadyDrivesCount: (count: number) => string;
    whatHappens: string;
    bothSmsTypes: string;
    bookingSmsInline: string;
    serviceReminderSmsInline: string;
    effectIncomingReceives: (name: string, smsTypes: string, cars: string) => string;
    effectIncomingSilenced: (name: string, cars: string) => string;
    /** Only for a driver who is not an admin: admins already see every car. */
    effectIncomingGainsAccess: (name: string, cars: string, carCount: number) => string;
    effectOutgoingStops: (name: string, cars: string) => string;
    effectOutgoingLosesAccess: (name: string, cars: string) => string;
    effectOutgoingRemaining: (name: string, count: number) => string;
    effectOutgoingNoneLeft: (name: string) => string;
    effectNoPreviousDriver: (cars: string) => string;
    effectMembershipUnchanged: string;
    turnOnSmsForMember: (name: string) => string;
    driversSaved: (count: number) => string;
    addCars: string;
    showCarsNeedingDriver: string;
    showAllCars: string;
    filteredToCarsNeedingAttention: string;
    /** Cars and reminders move to another member: the admin's pick, or the backend default when they cannot pick. */
    handoverCars: (name: string, carCount: number) => string;
    handoverProposals: (name: string, proposalCount: number) => string;
    /** The same, for a member leaving by themselves. */
    leaveHandoverCars: (carCount: number) => string;
    leaveHandoverProposals: (proposalCount: number) => string;
    moveCarsTo: string;
    chooseMember: string;
    noOtherMembers: string;
    /** For the paginated pickers inside the sheets; the overview's own tables take `tableTranslations`. */
    pagination: Partial<TablePaginationTranslations>;
  };

export const defaultNotificationGroupOverviewTranslations: NotificationGroupOverviewTranslations = {
  title: 'Members and drivers',
  description:
    "Administrators see all of the organization's cars and bookings and manage its members. Other members see only the cars they drive and the bookings they made or that include those cars. Each car has one driver who receives SMS about it, and a member can be the driver of several cars.",
  byCar: 'Cars',
  byCarDescription: 'Who drives each car. A car without a driver gets no SMS.',
  byMember: 'Members',
  byMemberDescription: "Each member's role, contact details and the cars they drive.",
  car: 'Car',
  member: 'Member',
  driver: 'Driver',
  driverTooltip:
    'The driver receives SMS notifications about this car and can see it and its bookings. Their settings determine whether they receive booking updates, service reminders, or both.',
  drives: 'Cars they drive',
  drivesTooltip:
    'The cars this member is the driver of. They receive SMS about these cars and can see them and their bookings.',
  otherCars: 'Other cars',
  searchCarsTablePlaceholder: 'Search by car or driver',
  searchMembersTablePlaceholder: 'Search by name, phone or email',
  noDriver: 'No driver',
  peopleCount: (count) => (count === 1 ? '1 person' : `${count} people`),
  carCount: (count) => {
    if (count === 0) {
      return 'No cars';
    }
    return count === 1 ? '1 car' : `${count} cars`;
  },
  bookingSms: 'Booking updates',
  serviceReminderSms: 'Service reminders',
  bookingSmsDescription: 'Confirmation, arrival time and completion of bookings for their cars.',
  serviceReminderSmsDescription: 'A heads-up when one of their cars is due for a seasonal wheel change.',
  smsTurnedOffWarning: 'Both SMS types are off, so this member gets no messages about the cars they drive.',
  smsTurnedOff: 'SMS turned off',
  carsWithoutDriver: (count) => (count === 1 ? '1 car has no driver' : `${count} cars have no driver`),
  carsWithoutDriverDescription: 'No one receives SMS notifications about these cars. Choose a driver in the Cars tab.',
  carsWithSilencedDriver: (count) =>
    count === 1
      ? '1 car has a driver with SMS notifications turned off'
      : `${count} cars have a driver with SMS notifications turned off`,
  carsWithSilencedDriverDescription: "Turn on the driver's SMS notifications or choose another driver.",
  editDriver: 'Change who drives this car',
  driverSheetDescription: 'Only the driver receives SMS about this car. Nothing is saved until you confirm.',
  searchMembersPlaceholder: 'Search by name or phone number',
  noMembersFound: 'No members found.',
  otherMembers: 'Other members',
  driverSaved: (licensePlate) => `Driver for ${licensePlate} updated`,
  moreCars: (count) => `+${count} more`,
  noDrivenCars: 'Drives no cars yet',
  noDrivenCarsDescription: 'Choose cars to make this member their driver. Only the driver receives SMS about a car.',
  back: 'Back',
  addCarsDescription: (name) => `Choose the cars ${name} should drive. Nothing is saved until you confirm.`,
  searchCars: 'Search by make, model or license plate',
  noCarsFound: 'No cars found.',
  currentDriver: (name) => `Current driver: ${name}`,
  currentDriverLabel: 'Current driver',
  cancel: 'Cancel',
  addMember: 'Add member',
  addMemberStepNumber: 'Number',
  addMemberStepNumberDescription: 'Who to add',
  addMemberStepPerson: 'Person',
  addMemberStepPersonDescription: 'Confirm or create',
  addMemberStepSettings: 'Settings',
  addMemberStepSettingsDescription: 'Role and SMS',
  next: 'Continue',
  memberFoundDescription: 'Check that this is the right person before adding them.',
  memberAlreadyInGroup: 'This person is already a member of the organization.',
  memberUnavailable: 'This number belongs to an account that cannot be added. Contact support.',
  memberNotFound: 'No account has this number yet. Fill in their name to create one.',
  memberEmailTaken: 'This email already belongs to another account. Leave it blank or use a different one.',
  emailOptional: 'Email (optional)',
  sendAddedSms: 'Tell them they were added',
  sendAddedSmsDescription: 'One SMS now, with a link to log in. Not a notification setting.',
  memberAdded: (name) => `${name} added`,
  memberEditAdminOnly: "Only an administrator can change another member's details.",
  roleAndDriversAdminOnly: 'Only an administrator can change roles and drivers.',
  adminEditsRest: 'An administrator changes the rest.',
  viewOnly: 'View only',
  role: 'Role',
  groupAdmin: 'Administrator',
  groupAdminDescription:
    "Sees all of the organization's cars, bookings and members' contact details. Can add and remove members, choose who drives which car, and change each member's SMS notifications. Members without this role see only the cars they drive and the bookings they made or that include those cars.",
  holderGroupAdminDescription:
    "Can add and remove members and change each member's SMS notifications. An administrator of this organization is also an employee.",
  employee: 'Employee',
  employeeDescription:
    "Can open this organization in the customer app and book the company's own cars. Cars leased to a customer stay on that customer's account.",
  driverEditAdminOnly: 'Only an administrator can choose who drives which car.',
  lastAdminHint: 'An organization needs at least one administrator.',
  makeAnotherAdminFirst: 'Make someone else an administrator before you leave.',
  accountOwnedByMember: (name) => `Only ${name} can change their own contact details.`,
  contactDetails: 'Contact details',
  firstName: 'First name',
  lastName: 'Last name',
  phoneNumber: 'Phone number',
  email: 'Email',
  notifications: 'SMS notifications',
  notificationsDescription: 'Sent only for the cars this member drives.',
  saveChanges: 'Save changes',
  edit: 'Edit',
  memberSaved: (name) => `${name} updated`,
  removeMember: 'Remove member',
  removeMemberDescription: (name) => `Remove ${name} from this organization? Their account is not affected.`,
  leaveGroup: 'Leave organization',
  leaveGroupDescription: 'You lose access to the cars and bookings in this organization. Your account is not affected.',
  remove: 'Remove',
  memberRemoved: (name) => `${name} removed from the organization`,
  primaryContactCannotBeRemoved:
    'We contact this person on behalf of the organization, so they cannot be removed. Contact support to point that at someone else first.',
  chooseDriverStep: 'Step 1 of 2 · Choose the new driver',
  chooseCarsStep: 'Step 1 of 2 · Choose the cars',
  reviewStep: 'Step 2 of 2 · Review and confirm',
  continueToReview: 'Continue',
  confirmDriverChange: 'Confirm change',
  takingOverFrom: (name) => `Taking over from ${name}`,
  alreadyDrivesCount: (count) =>
    count === 1
      ? 'Already drives 1 car, which stays as it is.'
      : `Already drives ${count} cars, which stay as they are.`,
  whatHappens: 'What happens when you confirm',
  bothSmsTypes: 'booking updates and service reminders',
  bookingSmsInline: 'booking updates',
  serviceReminderSmsInline: 'service reminders',
  effectIncomingReceives: (name, smsTypes, cars) => `${name} gets ${smsTypes} for ${cars}.`,
  effectIncomingSilenced: (name, cars) =>
    `${name} has SMS turned off, so nobody is notified about ${cars} until it is turned back on.`,
  effectIncomingGainsAccess: (name, cars, carCount) =>
    carCount === 1
      ? `${name} can now see ${cars} and its bookings.`
      : `${name} can now see ${cars} and their bookings.`,
  effectOutgoingStops: (name, cars) => `${name} stops getting SMS about ${cars}.`,
  effectOutgoingLosesAccess: (name, cars) =>
    `${name} can no longer see ${cars}, apart from bookings they made themselves.`,
  effectOutgoingRemaining: (name, count) =>
    count === 1 ? `${name} still drives 1 other car.` : `${name} still drives ${count} other cars.`,
  effectOutgoingNoneLeft: (name) => `${name} drives no cars after this.`,
  effectNoPreviousDriver: (cars) => `${cars} has no driver today, so nobody loses notifications.`,
  effectMembershipUnchanged: 'Everyone keeps their membership and their own SMS settings.',
  turnOnSmsForMember: (name) => `Turn on SMS for ${name}`,
  driversSaved: (count) => (count === 1 ? 'Driver updated' : `Drivers updated for ${count} cars`),
  addCars: 'Add cars',
  showCarsNeedingDriver: 'Show these cars',
  showAllCars: 'Show all cars',
  filteredToCarsNeedingAttention: 'Showing only cars that need attention',
  handoverCars: (name, carCount) =>
    `${name} drives ${carCount === 1 ? '1 car' : `${carCount} cars`}. Those cars move to another member.`,
  handoverProposals: (name, proposalCount) =>
    proposalCount === 1
      ? `${name}'s open service reminder goes to whoever takes over.`
      : `${name}'s ${proposalCount} open service reminders go to whoever takes over.`,
  leaveHandoverCars: (carCount) =>
    `You drive ${carCount === 1 ? '1 car' : `${carCount} cars`}. Those cars move to another member.`,
  leaveHandoverProposals: (proposalCount) =>
    proposalCount === 1
      ? 'Your open service reminder goes to whoever takes over.'
      : `Your ${proposalCount} open service reminders go to whoever takes over.`,
  moveCarsTo: 'Move cars to',
  chooseMember: 'Choose a member',
  noOtherMembers: 'Nobody else is in this organization. Add a member first.',
  ...defaultRemoveOrganizationCarTranslations,
  ...defaultOrganizationPrimaryContactTranslations,
  pagination: {}
};
